// ─────────────────────────────────────────────────────────────────────────────
// AiChatEffects
// ─────────────────────────────────────────────────────────────────────────────
// Side-effects for the AI Chat feature.
// Responsibilities:
//  - Drawer open → load conversation list from API
//  - Select conversation → fetch full detail from API
//  - Create draft conversation (local-only until first message)
//  - Delete conversation via API
//  - Send message → call AI service → update local state → refresh list
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, filter, last, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { AppState } from 'src/app/core/app.state';
import { IAiConversation, IAiMessage } from '../models/ai-chat.models';
import { AiChatService } from '../services/ai-chat.service';
import * as actions from './ai-chat.actions';
import * as selectors from './ai-chat.selectors';

@Injectable()
export class AiChatEffects {

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private aiChatService: AiChatService,
    private translate: TranslateService
  ) {}

  // ── Private helpers ─────────────────────────────────────────────────────────

  private generateId(): string {
    return 'draft-' + 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private buildEmptyConversation(): IAiConversation {
    const now = new Date().toISOString();
    return {
      id: this.generateId(),
      title: this.translate.instant('ai_chat.untitled'),
      messages: [],
      createdAt: now,
      updatedAt: now,
      sessionId: null,
    };
  }

  // ── Drawer → Load list ──────────────────────────────────────────────────────

  initOnOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.SetAiChatDrawerOpen, actions.ToggleAiChatDrawer),
      withLatestFrom(this.store.select(selectors.selectAiChatDrawerOpen)),
      filter(([, drawerOpen]) => drawerOpen),
      map(() => actions.LoadConversations())
    )
  );

  // ── Conversation list ───────────────────────────────────────────────────────

  loadConversations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadConversations),
      switchMap(() =>
        this.aiChatService.listConversations().pipe(
          map((conversations) => actions.LoadConversationsSuccess({ conversations })),
          catchError(() => of(actions.LoadConversationsFailure()))
        )
      )
    )
  );

  /** After list loads with no active chat: create a draft conversation. */
  createDraftOnInitialLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadConversationsSuccess),
      withLatestFrom(
        this.store.select(selectors.selectActiveConversationId),
        this.store.select(selectors.selectIsDraft)
      ),
      filter(([, activeId, isDraft]) => !activeId && !isDraft),
      map(() => actions.CreateDraftConversation())
    )
  );

  /** After a successful send on a draft, resolve the draft id to the real conversation id (session_id). */
  resolveDraftOnSend$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.SendMessageSuccess),
      withLatestFrom(this.store.select(selectors.selectIsDraft)),
      filter(([, isDraft]) => isDraft),
      filter(([{ conversation }]) => !!conversation.sessionId),
      map(([{ conversation }]) =>
        actions.ResolveDraftConversation({ realId: conversation.sessionId! })
      )
    )
  );

  // ── Select conversation ─────────────────────────────────────────────────────

  selectConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.SelectConversation),
      switchMap(({ id }) =>
        this.aiChatService.getConversation(id).pipe(
          map((conversation) => actions.SelectConversationSuccess({ conversation })),
          catchError(() => of(actions.SelectConversationFailure()))
        )
      )
    )
  );

  // ── Draft ───────────────────────────────────────────────────────────────────

  createDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CreateDraftConversation),
      map(() => actions.CreateDraftConversationSuccess({ conversation: this.buildEmptyConversation() }))
    )
  );

  // ── Delete ──────────────────────────────────────────────────────────────────

  deleteConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.DeleteConversation),
      mergeMap(({ id }) =>
        this.aiChatService.deleteConversation(id).pipe(
          map(() => actions.DeleteConversationSuccess({ id })),
          catchError(() => of(actions.DeleteConversationError()))
        )
      )
    )
  );

  /** After successful delete: select next conversation or create draft. */
  reselectAfterDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.DeleteConversationSuccess),
      withLatestFrom(this.store.select(selectors.selectActiveConversationId)),
      switchMap(([, activeId]) => {
        if (activeId) return of(actions.SelectConversation({ id: activeId }));
        return of(actions.CreateDraftConversation());
      })
    )
  );

  // ── Messaging ───────────────────────────────────────────────────────────────

  sendMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.SendMessage),
      withLatestFrom(this.store.select(selectors.selectActiveConversation)),
      filter(([, conv]) => conv !== null),
      switchMap(([{ text }, activeConversation]) => {
        const conversation = activeConversation!;
        const userNow = new Date().toISOString();
        const userMessage: IAiMessage = { role: 'user', text, timestamp: userNow };
        const messagesWithUser = [...conversation.messages, userMessage];

        const buildSuccess = (aiText: string, sessionId?: string, references?: {id: number; category: string; excerpt: string; source: string}[]): IAiConversation => {
          const aiNow = new Date().toISOString();
          const aiMessage: IAiMessage = { role: 'ai', text: aiText, references: references, timestamp: aiNow };
          return {
            ...conversation,
            messages: [...messagesWithUser, aiMessage],
            updatedAt: aiNow,
            sessionId: sessionId ?? conversation.sessionId ?? null,
          };
        };

        if (environment.aiChatUseStream) {
          let accumulated = '';
          let streamSessionId: string | undefined;

          return this.aiChatService.sendMessageStream(messagesWithUser, conversation.sessionId).pipe(
            tap((event) => {
              if (event.type === 'text') {
                accumulated += event.text ?? '';
                this.aiChatService.streamChunk$.next(event.text ?? '');
              } else if (event.type === 'session') {
                streamSessionId = event.sessionId;
              }
            }),
            last(),
            map(() => actions.SendMessageSuccess({
              conversation: buildSuccess(accumulated, streamSessionId ?? conversation.sessionId ?? undefined),
            })),
            catchError(() => of(actions.SendMessageError()))
          );
        }

        return this.aiChatService.sendMessage(messagesWithUser, conversation.sessionId).pipe(
          map((response) =>
            actions.SendMessageSuccess({
              conversation: buildSuccess(response.text, response.session_id ?? conversation.sessionId ?? undefined, response.references),
            })
          ),
          catchError(() => of(actions.SendMessageError()))
        );
      })
    )
  );

  /** After a successful send, refresh the conversation list (updates ordering, title, preview). */
  refreshListAfterSend$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.SendMessageSuccess),
      map(() => actions.LoadConversations())
    )
  );
}
